/**
 * Direct to checkout — mat PDP.
 *
 * Built for the Intelligems "direct to checkout (mat)" test. It does nothing
 * unless the visitor is in the treatment group, which the onsite edit signals
 * with
 *
 *   :root { --gw-direct-checkout: 1; }
 *
 * (or `window.GW_DIRECT_CHECKOUT = true` from a JS edit). In the treatment, a
 * successful Add to Cart from this page's product form (the main buy button or
 * the sticky ATC) goes straight to checkout instead of opening the cart drawer.
 *
 * The theme's product-form still does the add, so validation, out-of-stock
 * errors and the quantity-consolidation wrapper in custom-js.liquid all work as
 * usual. This only replaces what happens after a successful add: the drawer's
 * renderContents (which paints and opens it) is swapped for the redirect while
 * an add from this page is in flight. A failed add leaves the shopper on the
 * page with the theme's error, exactly as in Control.
 *
 * layout/theme.liquid loads this on the mat PDP only, and passes the product's
 * variant ids so add-ons and other products' forms keep the normal drawer.
 *
 * Once switched on, it stays on for the rest of the tab's session
 * (sessionStorage), so a shopper who comes back from checkout, by the back
 * button or any other route, still gets direct to checkout even if the
 * Intelligems CSS hasn't applied yet on that page load. A Control visitor never
 * sees the CSS, so is never remembered as on.
 *
 * QA: ?gw_dc_preview=1 switches it on, and it then stays on for the tab;
 * ?gw_dc_preview=0 switches it off again. On a draft theme, open its preview
 * first, then add the param to the mat page URL. Check Control in a fresh
 * private window (or after ?gw_dc_preview=0), not in a tab that was on.
 */
(function () {
  'use strict';

  if (window.__gwDirectCheckoutLoaded) return;
  window.__gwDirectCheckoutLoaded = true;

  var script = document.currentScript;
  var dataVariants = ((script && script.dataset.variants) || '').split(',').filter(Boolean);
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  // Follows the store's route root, so checkout opens in the visitor's market.
  var CHECKOUT_URL = root + 'checkout';
  // How long after the click an add still counts as "from this page". Matches
  // the safety net's timeout in atc-safety-net.liquid.
  var PENDING_MS = 15000;
  // Longest wait for the free socket tester (gw-tester-gift.js) to land before
  // leaving for checkout anyway. Its add normally takes about 1.2s.
  var GIFT_WAIT_MS = 5000;
  var SESSION_KEY = 'gw_direct_checkout';

  function remembered() {
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function remember(value) {
    try {
      if (value) sessionStorage.setItem(SESSION_KEY, '1');
      else sessionStorage.removeItem(SESSION_KEY);
    } catch (e) {}
  }

  var preview = false;
  try {
    var param = new URLSearchParams(location.search).get('gw_dc_preview');
    if (param === '0') remember(false);
    preview = param === '1';
  } catch (e) {}

  // On now (preview, JS edit or the treatment CSS), which is then remembered
  // for the session; or on earlier in this session.
  function on() {
    var live = preview || window.GW_DIRECT_CHECKOUT === true ||
      getComputedStyle(document.documentElement).getPropertyValue('--gw-direct-checkout').trim() === '1';
    if (live) remember(true);
    return live || remembered();
  }

  // Record the treatment on page view, not only on click, so a visitor who
  // goes to the cart some other way and comes back is still on.
  on();
  window.addEventListener('load', on);

  var pending = null; // { form, until } while an add from this page is in flight
  var going = false;

  function isPending() {
    return !!pending && Date.now() < pending.until;
  }

  // The page product's variant ids, from the script tag, or from Shopify's own
  // product meta if the lazy loader re-created the tag without its attributes.
  function variants() {
    if (dataVariants.length) return dataVariants;
    var product = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
    return ((product && product.variants) || []).map(function (v) { return String(v.id); });
  }

  // This product's own buy form: not the drawer's add-ons, not another
  // product's quick add.
  function isOurForm(form) {
    if (!form || form.tagName !== 'FORM' || !form.closest('product-form')) return false;
    if (form.closest('m-cart-drawer')) return false;
    var id = form.querySelector('[name="id"]');
    return !!id && variants().indexOf(String(id.value)) !== -1;
  }

  function go() {
    if (going) return;
    going = true;
    var wrap = pending && pending.form.closest('.product-form');
    pending = null;
    // The theme's `finally` clears the spinner after the add; put it back so the
    // button stays busy while checkout loads.
    if (wrap) setTimeout(function () { wrap.classList.add('adding'); }, 0);
    // The free tester is added in response to this same add; leaving now would
    // cancel that request and checkout would open without the gift.
    var gift = typeof window.gwTesterGiftSettled === 'function' ? window.gwTesterGiftSettled() : Promise.resolve();
    var cap = new Promise(function (resolve) { setTimeout(resolve, GIFT_WAIT_MS); });
    Promise.race([gift, cap]).then(function () {
      location.assign(CHECKOUT_URL);
    });
  }

  // Swaps the drawer's paint-and-open for the redirect while an add is pending.
  // Set on the element itself, so it works whether or not cart.min.js (lazy
  // loaded) has upgraded the drawer yet; any other call falls through to the
  // theme's method.
  function guardDrawer() {
    var drawer = document.querySelector('m-cart-drawer');
    if (!drawer || drawer.__gwDirectCheckout) return;
    drawer.__gwDirectCheckout = true;
    drawer.renderContents = function () {
      if (isPending()) return go();
      return Object.getPrototypeOf(this).renderContents.apply(this, arguments);
    };
  }

  // Fallback for a page without the drawer (the theme then never calls
  // renderContents), and the place a failed add clears the pending state.
  var subscribed = false;
  function subscribe() {
    var events = window.MinimogEvents;
    var names = window.MinimogTheme && window.MinimogTheme.pubSubEvents;
    if (subscribed || !events || !names) return;
    subscribed = true;
    events.subscribe(names.cartUpdate, function () {
      if (isPending()) go();
    });
    events.subscribe(names.cartError, function () {
      pending = null;
    });
  }

  // Window capture runs ahead of the theme's and the safety net's listeners. A
  // click the safety net buffers is replayed as a fresh submit, which lands
  // here again.
  window.addEventListener('submit', function (event) {
    var form = event.target;
    if (going || !isOurForm(form) || !on()) return;
    pending = { form: form, until: Date.now() + PENDING_MS };
    guardDrawer();
    subscribe();
  }, true);

  // Back button from checkout restores this page from the bfcache: unstick the
  // button and repaint the drawer, which never saw the add, without opening it.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    going = false;
    pending = null;
    document.querySelectorAll('.product-form.adding').forEach(function (el) {
      el.classList.remove('adding');
    });
    var drawer = document.querySelector('m-cart-drawer');
    if (drawer && typeof drawer.onCartDrawerUpdate === 'function') drawer.onCartDrawerUpdate();
  });
})();
